import { cloneDeep, orderBy } from 'lodash'
import { AttributeTypeEnum, ProductBatchTypeEnum } from '@c2b/muven-commons'
import { ProductAttributeModel } from './ProductAttributeModel'
import { SubscriberBackendMonitor } from '../../subscriber/models/SubscriberModel'
import { ProductBatchItemPriceValuePayloadModel } from './ProductBatchItemPriceValuePayloadModel';

export class ProductBatchItemProductModel {
    static normalizeV1(product:ProductBatchItemProductModel, subscriber: SubscriberBackendMonitor):ProductBatchItemProductModel {
        const p = cloneDeep(product);

        if (!subscriber.isBackendBimer()) {
            return p
        }

        p.description = p.description || p.children?.[0]?.description || ""
        p.descriptionShort = p.descriptionShort || p.children?.[0]?.descriptionShort || ""
        p.descriptionLong = p.descriptionLong || p.children?.[0]?.descriptionLong || ""

        const hasGrid = (p.children || []).find((ch) => {
            return ch.hasAttributeTypeGrid()
        })

        if (!hasGrid) {
            p.skuCode = p.skuCode || p.children?.[0]?.skuCode || ""
            p.eanCode = p.eanCode || p.children?.[0]?.eanCode || ""
            p.ncmCode = p.eanCode || p.children?.[0]?.ncmCode || ""
            p.firstChildId = p.children?.[0]?.id
            p.children = [];
        }

        return p;
    }

    id?:string|number = undefined
    firstChildId?:string|number // only exists when it's a simple product from Alterdata's Backend Bimer
    idSource?:string|number = undefined
    idSourceSku?:string|number = undefined
    name:string = ''
    skuCode:string = ''
    codeSource?:string = undefined
    description?:string = ''
    descriptionLong?:string = ''
    descriptionShort?:string = ''
    eanCode?:string = ''
    ncmCode?:string = ''
    isbnCode?:string = ''
    isActive?:boolean = true
    parent?:ProductBatchItemProductModel = undefined
    children: ProductBatchItemProductModel[] = []
    attributes: ProductAttributeModel[] = []
    isSku: boolean = false
    loadingListAction?: boolean = false
    isGridVisible?: boolean = false    
    isBeingModified: boolean = false;
    batchType: ProductBatchTypeEnum
    listPrice: number = 0;
    sellPrice: number = 0;
    payload: {
        [key: string]: any;
    }

    constructor(p:Partial<ProductBatchItemProductModel>, batchType: ProductBatchTypeEnum) {
        Object.assign(this, p)
        this.batchType = batchType

        this._parseAttributes()
        this._parseChildren()
        this._parsePayload()

        if (!!this.parent) {
            this._parseParent()
        }
    }

    setBeingModifiedStatus(st: boolean):void {
        this.isBeingModified = st;
    }

    getVariationsStringified():string {
        let variationInfo = ""
        const grid = this.getAttributeTypeGridList()

        if (!grid || !grid.length) {
            return ""
        }

        for (const item of grid) {
            variationInfo += `${item.attribute!.name} ${item.value}, `
        }

        return variationInfo.replace(/, $/, '')
    }

    hasChildren():boolean {
        return !!this.children.length;
    }

    hasMoreThanOneChildren():boolean {
        return this.children.length > 1;
    }
    
    hasCodeSource():boolean {
        return !!this.codeSource;
    }

    hasAttributes():boolean {
        return !!this.attributes.length
    }

    hasAttributeTypeGrid():boolean {
        if (!this.attributes.length) {
            return false
        }

        return !!this.attributes.find((attr) => {
            return attr.attribute?.type === AttributeTypeEnum.Grid
        })
    }

    getAttributeTypeGridList():ProductAttributeModel[] {
        if (!this.attributes.length) {
            return []
        }

        return orderBy(this.attributes.filter((prodAttr) => {
            return prodAttr.attribute!.isGrid() 
        }), ['attribute.name'], ['asc'])
    }

    private _parseParent():void {
        this.parent = new ProductBatchItemProductModel(this.parent as ProductBatchItemProductModel, this.batchType)
    }

    private _parseAttributes():void {
        this.attributes = this.attributes.map((prodAttr) => {
            return new ProductAttributeModel(prodAttr);
        })
    }

    private _parseChildren():void {
        this.children = this.children.map((p) => {
            return new ProductBatchItemProductModel(p, this.batchType);
        })
    }

    private _parsePayload():void {
        switch (this.batchType) {
            case ProductBatchTypeEnum.UpdateChannelPriceValue:
                this.payload = new ProductBatchItemPriceValuePayloadModel(this.payload)
        }
    }
}
